import type { ScormInteractionData } from './interactions'
import { iso8601DurationToMs, msToIso8601Duration } from './durations'

/** Garde-fou de plage pour les scores absolus (raw/min/max). */
const MAX_SCORE_ABS = 1_000_000

/**
 * Parse et valide un score SCORM. Renvoie null si la valeur est absente, non
 * finie (NaN/Infinity) ou hors d'une plage plausible — pour éviter de propager
 * des valeurs poubelle (ex. 3.99e+49) émises par un module/driver buggé.
 */
function sanitizeScore(value: string | undefined, max = MAX_SCORE_ABS): number | null {
  if (!value) return null
  const n = Number.parseFloat(value)
  if (!Number.isFinite(n) || Math.abs(n) > max) return null
  return n
}

export type ScormData = {
  completionStatus?: string
  successStatus?: string
  scoreRaw?: number | null
  scoreMin?: number | null
  scoreMax?: number | null
  scoreScaled?: number | null
  totalTime?: string | null
  sessionTime?: string | null
  suspendData?: string | null
  location?: string | null
  /** Interactions modifiées depuis le commit précédent (#158) ; absent s'il n'y en a aucune. */
  interactions?: ScormInteractionData[]
}

/**
 * Rollup des objectifs : si le contenu ne pose pas cmi.success_status mais
 * renseigne ses objectifs (cmi.objectives.N.success_status), une valeur
 * unique passed/failed fait foi. Des valeurs contradictoires → aucun signal
 * (on ne devine pas quel objectif est le principal).
 */
function objectivesSuccess(data: Record<string, string>): string | null {
  const values = new Set<string>()
  for (const [key, value] of Object.entries(data)) {
    if (/^cmi\.objectives\.\d+\.success_status$/.test(key) && (value === 'passed' || value === 'failed')) {
      values.add(value)
    }
  }
  return values.size === 1 ? [...values][0] : null
}

/**
 * Ce que le bridge envoie au serveur à chaque `Commit`, lu dans son modèle de
 * données : statuts traduits vers l'API, scores assainis, temps total cumulé.
 */
export function commitPayload(
  data: Record<string, string>,
  { initialTotalTimeMs, successSetByContent }: { initialTotalTimeMs: number, successSetByContent: boolean },
): ScormData {
  const completionStatusMap: Record<string, string> = {
    completed: 'COMPLETED',
    incomplete: 'INCOMPLETE',
    'not attempted': 'NOT_ATTEMPTED',
    unknown: 'UNKNOWN',
  }

  const successStatusMap: Record<string, string> = {
    passed: 'PASSED',
    failed: 'FAILED',
    unknown: 'UNKNOWN',
  }

  // Accumulate total_time = previous total + current session_time (per SCORM 2004 spec)
  let accumulatedTotalTime: string | null = data['cmi.total_time'] || null
  const sessionTimeStr = data['cmi.session_time'] || null
  if (sessionTimeStr) {
    const sessionMs = iso8601DurationToMs(sessionTimeStr)
    accumulatedTotalTime = msToIso8601Duration(initialTotalTimeMs + sessionMs)
  }

  const successValue = successSetByContent
    ? data['cmi.success_status']
    : objectivesSuccess(data)

  return {
    completionStatus: completionStatusMap[data['cmi.completion_status'] || 'not attempted'] || 'NOT_ATTEMPTED',
    successStatus: successValue ? successStatusMap[successValue] : undefined,
    scoreRaw: sanitizeScore(data['cmi.score.raw']),
    scoreMin: sanitizeScore(data['cmi.score.min']),
    scoreMax: sanitizeScore(data['cmi.score.max']),
    // cmi.score.scaled est borné à [-1, 1] par la spec SCORM 2004.
    scoreScaled: sanitizeScore(data['cmi.score.scaled'], 1),
    totalTime: accumulatedTotalTime,
    sessionTime: sessionTimeStr,
    suspendData: data['cmi.suspend_data'] || null,
    location: data['cmi.location'] || null,
  }
}
